import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Instagram, Mail } from "lucide-react";
import { VILLA } from "../lib/api";
import { useLanguage, LANGUAGES } from "../lib/i18n";
import WhatsAppButton from "./WhatsAppButton";
import BookingComButton from "./BookingComButton";
import MobileEnquireBar from "./MobileEnquireBar";
import PalmMark from "./PalmMark";
import LanguageSwitcher from "./LanguageSwitcher";

const NAV = [
    { to: "/", key: "nav.home" },
    { to: "/rooms", key: "nav.rooms" },
    { to: "/gallery", key: "nav.gallery" },
    { to: "/amenities", key: "nav.amenities" },
    { to: "/location", key: "nav.location" },
    { to: "/contact", key: "nav.contact" },
];

export const Layout = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();
    const { lang, setLang, t } = useLanguage();
    const isHome = pathname === "/";

    useEffect(() => {
        const h = () => setScrolled(window.scrollY > 40);
        h();
        window.addEventListener("scroll", h);
        return () => window.removeEventListener("scroll", h);
    }, []);

    useEffect(() => {
        setOpen(false);
        window.scrollTo(0, 0);
    }, [pathname]);

    const solid = scrolled || !isHome;

    return (
        <div className="min-h-screen flex flex-col bg-background">
            <header
                data-testid="site-header"
                className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                    solid
                        ? "bg-background/90 backdrop-blur-md border-b border-border"
                        : "bg-transparent"
                }`}
            >
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-20 flex items-center justify-between">
                    <Link
                        to="/"
                        data-testid="logo-link"
                        className={`flex items-center gap-3 leading-none ${
                            solid ? "text-primary" : "text-white"
                        }`}
                    >
                        <PalmMark size={28} className="shrink-0" />
                        <span className="font-display text-2xl tracking-wider">
                            Villa Alam Lembongan
                        </span>
                    </Link>

                    <nav className="hidden lg:flex items-center gap-10">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                data-testid={`nav-${n.key.replace("nav.", "")}`}
                                className={({ isActive }) =>
                                    `text-xs tracking-[0.2em] uppercase transition-colors ${
                                        isActive
                                            ? "text-secondary"
                                            : solid
                                            ? "text-foreground hover:text-secondary"
                                            : "text-white hover:text-secondary"
                                    }`
                                }
                                end={n.to === "/"}
                            >
                                {t(n.key)}
                            </NavLink>
                        ))}
                        <LanguageSwitcher solid={solid} />
                    </nav>

                    <button
                        data-testid="mobile-menu-toggle"
                        className={`lg:hidden p-2 ${
                            solid ? "text-primary" : "text-white"
                        }`}
                        onClick={() => setOpen(!open)}
                        aria-label="menu"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>

                {open && (
                    <div
                        data-testid="mobile-menu"
                        className="lg:hidden bg-background border-t border-border"
                    >
                        <div className="px-6 py-6 flex flex-col gap-5">
                            {NAV.map((n) => (
                                <NavLink
                                    key={n.to}
                                    to={n.to}
                                    data-testid={`mobile-nav-${n.key.replace("nav.", "")}`}
                                    className={({ isActive }) =>
                                        `text-sm tracking-[0.2em] uppercase ${
                                            isActive
                                                ? "text-secondary"
                                                : "text-foreground"
                                        }`
                                    }
                                    end={n.to === "/"}
                                >
                                    {t(n.key)}
                                </NavLink>
                            ))}
                            {/* Mobile language picker — grid of all 10 options */}
                            <div className="pt-4 mt-2 border-t border-border">
                                <div className="overline text-muted-foreground mb-3">
                                    {t("label.language")}
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    {LANGUAGES.map((l) => {
                                        const selected = l.code === lang;
                                        return (
                                            <button
                                                key={l.code}
                                                type="button"
                                                data-testid={`mobile-lang-${l.code}`}
                                                onClick={() => {
                                                    setLang(l.code);
                                                    setOpen(false);
                                                }}
                                                className={`flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors ${
                                                    selected
                                                        ? "bg-primary text-white"
                                                        : "bg-muted text-foreground"
                                                }`}
                                            >
                                                <span className="inline-flex w-6 h-6 items-center justify-center text-[10px] tracking-wider bg-white/10 font-medium">
                                                    {l.label}
                                                </span>
                                                <span className="truncate">{l.name}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </header>

            <main className="flex-1 pb-20 lg:pb-0">
                <Outlet />
            </main>

            <BookingComButton />
            <WhatsAppButton />
            <MobileEnquireBar />

            <footer
                data-testid="site-footer"
                className="bg-primary text-white/80 mt-20"
            >
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 grid md:grid-cols-4 gap-12">
                    <div className="md:col-span-2">
                        <div className="flex items-center gap-3 text-white">
                            <PalmMark size={34} className="shrink-0 text-secondary" />
                            <div className="font-display text-4xl tracking-wider">
                                Villa Alam Lembongan
                            </div>
                        </div>
                        <p className="mt-6 text-sm leading-relaxed max-w-md font-light">
                            {t("footer.tagline")}
                        </p>
                    </div>
                    <div>
                        <div className="overline text-white/60 mb-5">
                            {t("label.explore")}
                        </div>
                        <ul className="space-y-3 text-sm">
                            {NAV.slice(1).map((n) => (
                                <li key={n.to}>
                                    <Link
                                        to={n.to}
                                        className="hover:text-secondary transition-colors"
                                    >
                                        {t(n.key)}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <div className="overline text-white/60 mb-5">
                            {t("label.contact")}
                        </div>
                        <div className="space-y-3 text-sm">
                            <a
                                href={`mailto:${VILLA.email}`}
                                className="flex items-center gap-2 hover:text-secondary transition-colors"
                                data-testid="footer-email"
                            >
                                <Mail size={14} /> {VILLA.email}
                            </a>
                            <a
                                href="https://instagram.com/villa_alam_lembongan"
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center gap-2 hover:text-secondary transition-colors"
                            >
                                <Instagram size={14} /> @villa_alam_lembongan
                            </a>
                        </div>
                    </div>
                </div>
                <div className="border-t border-white/10">
                    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/50">
                        <div>
                            © {new Date().getFullYear()} Villa Alam Lembongan.
                            All rights reserved.
                        </div>
                        <Link
                            to="/admin/login"
                            data-testid="admin-link"
                            className="hover:text-white/80 transition-colors tracking-widest uppercase"
                        >
                            Admin
                        </Link>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Layout;
